import { useState, useEffect } from "react";
import { Globe, Code, TrendingUp, Bot, Check, ArrowRight, ArrowLeft, Calendar, Clock, Video } from "lucide-react";
import { Calendar as CalendarComponent } from "./ui/calendar";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const services = [
  {
    id: "web",
    name: "Web Development",
    icon: Globe,
    description: "Custom websites, e-commerce, and web applications"
  },
  {
    id: "software",
    name: "Custom Software",
    icon: Code,
    description: "CRM systems, dashboards, and business automation"
  },
  {
    id: "ai",
    name: "AI Automation",
    icon: Bot,
    description: "Chatbots, process automation, and AI integration"
  },
  {
    id: "marketing",
    name: "Growth Marketing",
    icon: TrendingUp,
    description: "SEO, PPC campaigns, and content strategy"
  }
];

const timeSlots = [
  "9:00 AM", "9:30 AM", "10:00 AM", "10:30 AM", "11:00 AM", "11:30 AM",
  "1:00 PM", "1:30 PM", "2:00 PM", "2:30 PM", "3:00 PM", "3:30 PM", "4:00 PM", "4:30 PM"
];

export default function ConsultationForm({ preSelectedServiceId }) {
  const preSelected = preSelectedServiceId ? services.find(s => s.id === preSelectedServiceId) : null;

  const [step, setStep] = useState(preSelected ? 2 : 1);
  const [selectedService, setSelectedService] = useState(preSelected || null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [wantsCall, setWantsCall] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    budget: "",
    timeline: "",
    message: "",
    website: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const formatPhoneNumber = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 3) return numbers.length ? `(${numbers}` : '';
    if (numbers.length <= 6) return `(${numbers.slice(0, 3)})${numbers.slice(3)}`;
    return `(${numbers.slice(0, 3)})${numbers.slice(3, 6)}-${numbers.slice(6, 10)}`;
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhoneNumber(e.target.value);
    setFormData(prev => ({ ...prev, phone: formatted }));
  };

  useEffect(() => {
    if (isSuccess && typeof window.gtag === 'function') {
      window.gtag('event', 'conversion', {
        'send_to': 'AW-17995521753/KyddCL6_zIUcENm994RD',
        'value': 1.0,
        'currency': 'CAD'
      });
    }
  }, [isSuccess]);

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (wantsCall && (!selectedDate || !selectedTime)) {
      toast.error("Please select a date and time for your consultation");
      return;
    }
    setIsSubmitting(true);
    try {
      const consultationData = {
        service: selectedService?.name || "General Inquiry",
        answers: {
          budget: formData.budget,
          timeline: formData.timeline,
          ...(wantsCall && {
            consultationDate: selectedDate?.toLocaleDateString(),
            consultationTime: selectedTime
          })
        },
        additionalInfo: formData.message,
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        company: formData.company,
        website: formData.website,
        submittedAt: new Date().toISOString()
      };
      await axios.post(`${API}/consultation`, consultationData);
      setIsSuccess(true);
      toast.success("Thank you for choosing Averoan!", {
        description: "A confirmation email has been sent. Please check your inbox (and spam folder).",
        duration: 8000
      });
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const disabledDays = [
    { before: new Date() },
    { dayOfWeek: [0, 6] }
  ];

  if (isSuccess) {
    return (
      <div className="max-w-2xl mx-auto text-center py-16">
        <div
          className="w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-8"
          style={{ background: 'rgba(212, 255, 0, 0.1)', border: '2px solid #D4FF00' }}
        >
          <Check size={48} style={{ color: '#D4FF00' }} />
        </div>
        <h2
          className="text-4xl md:text-6xl font-bold mb-6"
          style={{ fontFamily: '"Clash Display", sans-serif' }}
          data-testid="consultation-success-title"
        >
          {wantsCall ? "You're " : "Request "}
          <span style={{ color: '#D4FF00' }}>{wantsCall ? "booked!" : "sent!"}</span>
        </h2>
        {wantsCall ? (
          <>
            <p className="text-lg mb-2" style={{ color: '#A1A1AA' }}>
              Your 30-minute consultation is scheduled for:
            </p>
            <p className="text-2xl font-bold mb-8" style={{ color: '#D4FF00' }}>
              {selectedDate?.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })} at {selectedTime}
            </p>
            <p className="text-sm mb-8" style={{ color: '#A1A1AA' }}>
              You'll receive a confirmation email with the meeting link shortly.
            </p>
          </>
        ) : (
          <p className="text-lg mb-8" style={{ color: '#A1A1AA' }}>
            We'll review your request and get back to you within 24 hours.
          </p>
        )}
        <div className="flex flex-wrap justify-center gap-4">
          <a href="/" className="btn-primary" data-testid="consultation-success-home-btn">
            Back to Home <ArrowRight size={16} />
          </a>
          <a href="/portfolio" className="btn-outline" data-testid="consultation-success-portfolio-btn">
            View Portfolio
          </a>
        </div>
      </div>
    );
  }

  const totalSteps = preSelected ? 2 : 3;
  const displayStep = preSelected ? step - 1 : step;

  return (
    <div>
      {/* Progress Steps */}
      <div className="flex items-center justify-center gap-2 md:gap-4 mb-8" data-testid="consultation-progress">
        {(preSelected ? [2, 3] : [1, 2, 3]).map((s, idx) => (
          <div key={s} className="flex items-center">
            <div
              className={`w-8 h-8 md:w-10 md:h-10 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
                step >= s ? 'bg-[#D4FF00] text-black' : 'bg-white/10 text-white/50'
              }`}
            >
              {idx + 1}
            </div>
            <span className={`ml-2 text-xs md:text-sm font-medium hidden sm:block ${step >= s ? 'text-white' : 'text-white/50'}`}>
              {s === 1 ? 'Service' : s === 2 ? 'Details' : 'Consultation'}
            </span>
            {idx < totalSteps - 1 && <div className="w-8 md:w-16 h-[2px] mx-2 md:mx-4 bg-white/20" />}
          </div>
        ))}
      </div>

      <div className="max-w-4xl mx-auto">

        {/* Step 1: Select Service */}
        {step === 1 && !preSelected && (
          <div>
            <h2
              className="text-2xl md:text-3xl font-bold mb-8 text-center"
              style={{ fontFamily: '"Clash Display", sans-serif' }}
            >
              What do you <span style={{ color: '#D4FF00' }}>need?</span>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
              {services.map((service) => (
                <button
                  key={service.id}
                  onClick={() => setSelectedService(service)}
                  data-testid={`service-option-${service.id}`}
                  className={`p-6 rounded-2xl text-left transition-all ${
                    selectedService?.id === service.id
                      ? 'bg-[#D4FF00] text-black'
                      : 'bg-[#18181B] border border-white/10 hover:border-[#D4FF00]/50'
                  }`}
                >
                  <service.icon
                    size={32}
                    className={`mb-4 ${selectedService?.id === service.id ? 'text-black' : 'text-[#D4FF00]'}`}
                  />
                  <h3 className="text-lg font-bold mb-2">{service.name}</h3>
                  <p className={`text-sm ${selectedService?.id === service.id ? 'text-black/70' : 'text-zinc-500'}`}>
                    {service.description}
                  </p>
                </button>
              ))}
            </div>
            <div className="flex justify-center">
              <button
                onClick={() => setStep(2)}
                disabled={!selectedService}
                className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="consultation-step1-continue"
              >
                Continue <ArrowRight size={16} />
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Project Details */}
        {step === 2 && (
          <div
            className="p-6 md:p-10 rounded-2xl"
            style={{ background: '#18181B', border: '1px solid rgba(255,255,255,0.1)' }}
          >
            {!preSelected && (
              <button
                onClick={() => setStep(1)}
                className="flex items-center gap-2 text-sm text-zinc-500 hover:text-white mb-6 transition-colors"
                data-testid="consultation-step2-back"
              >
                <ArrowLeft size={16} /> Back
              </button>
            )}

            {preSelected && (
              <div className="flex items-center gap-3 mb-6 p-3 rounded-xl" style={{ background: 'rgba(212,255,0,0.05)', border: '1px solid rgba(212,255,0,0.15)' }}>
                <preSelected.icon size={20} style={{ color: '#D4FF00' }} />
                <span className="text-sm font-medium">{preSelected.name}</span>
                <span className="text-xs ml-auto" style={{ color: '#D4FF00' }}>Selected</span>
              </div>
            )}

            <h2
              className="text-2xl md:text-3xl font-bold mb-8"
              style={{ fontFamily: '"Clash Display", sans-serif' }}
            >
              Tell us about your <span style={{ color: '#D4FF00' }}>project</span>
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-xs uppercase tracking-wider mb-3" style={{ color: '#D4FF00' }}>Full Name *</label>
                <input type="text" name="name" value={formData.name} onChange={handleChange} className="input-field" placeholder="John Smith" required data-testid="consultation-name" />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider mb-3" style={{ color: '#D4FF00' }}>Email *</label>
                <input type="email" name="email" value={formData.email} onChange={handleChange} className="input-field" placeholder="john@company.com" required data-testid="consultation-email" />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-xs uppercase tracking-wider mb-3" style={{ color: '#D4FF00' }}>Phone</label>
                <input type="tel" name="phone" value={formData.phone} onChange={handlePhoneChange} className="input-field" placeholder="(000)000-0000" maxLength={13} data-testid="consultation-phone" />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider mb-3" style={{ color: '#D4FF00' }}>Company</label>
                <input type="text" name="company" value={formData.company} onChange={handleChange} className="input-field" placeholder="Your company name" data-testid="consultation-company" />
              </div>
            </div>

            {/* Not for people. Hidden, unlabelled, skipped by the tab key
                and excluded from autofill - a bot fills every input it finds
                in the HTML, and filling this one is the giveaway. The server
                bins anything that arrives with it set. */}
            <input
              type="text"
              name="website"
              value={formData.website}
              onChange={handleChange}
              tabIndex="-1"
              autoComplete="off"
              aria-hidden="true"
              data-testid="spam-trap"
              style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', opacity: 0 }}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-xs uppercase tracking-wider mb-3" style={{ color: '#D4FF00' }}>Budget Range</label>
                <select name="budget" value={formData.budget} onChange={handleChange} className="input-field bg-transparent" data-testid="consultation-budget">
                  <option value="">Select budget</option>
                  <option value="Under $1,000">Under $1,000</option>
                  <option value="$1,000 - $5,000">$1,000 - $5,000</option>
                  <option value="$5,000 - $10,000">$5,000 - $10,000</option>
                  <option value="$10,000 - $25,000">$10,000 - $25,000</option>
                  <option value="$25,000+">$25,000+</option>
                </select>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider mb-3" style={{ color: '#D4FF00' }}>Timeline</label>
                <select name="timeline" value={formData.timeline} onChange={handleChange} className="input-field bg-transparent" data-testid="consultation-timeline">
                  <option value="">Select timeline</option>
                  <option value="ASAP">ASAP</option>
                  <option value="1-2 weeks">1-2 weeks</option>
                  <option value="1 month">1 month</option>
                  <option value="2-3 months">2-3 months</option>
                  <option value="Flexible">Flexible</option>
                </select>
              </div>
            </div>

            <div className="mb-8">
              <label className="block text-xs uppercase tracking-wider mb-3" style={{ color: '#D4FF00' }}>Project Details</label>
              <textarea name="message" value={formData.message} onChange={handleChange} className="input-field min-h-[120px] resize-none" placeholder="Tell us about your project, goals, and any specific requirements..." data-testid="consultation-message" />
            </div>

            <div className="flex justify-end">
              <button
                onClick={() => setStep(3)}
                disabled={!formData.name || !formData.email}
                className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="consultation-step2-continue"
              >
                Continue <ArrowRight size={16} />
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Book Consultation */}
        {step === 3 && (
          <div
            className="p-6 md:p-10 rounded-2xl"
            style={{ background: '#18181B', border: '1px solid rgba(255,255,255,0.1)' }}
          >
            <button
              onClick={() => setStep(2)}
              className="flex items-center gap-2 text-sm text-zinc-500 hover:text-white mb-6 transition-colors"
              data-testid="consultation-step3-back"
            >
              <ArrowLeft size={16} /> Back
            </button>

            <h2
              className="text-2xl md:text-3xl font-bold mb-4"
              style={{ fontFamily: '"Clash Display", sans-serif' }}
            >
              Book a free <span style={{ color: '#D4FF00' }}>consultation?</span>
            </h2>
            <p className="text-zinc-500 mb-8">
              Schedule a 30-minute video call to discuss your project in detail. It's completely free!
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              <div className="flex items-center gap-3 p-4 rounded-xl bg-white/5">
                <Clock size={20} style={{ color: '#D4FF00' }} />
                <span className="text-sm">30 Minutes</span>
              </div>
              <div className="flex items-center gap-3 p-4 rounded-xl bg-white/5">
                <Video size={20} style={{ color: '#D4FF00' }} />
                <span className="text-sm">Video Call</span>
              </div>
              <div className="flex items-center gap-3 p-4 rounded-xl bg-white/5">
                <Calendar size={20} style={{ color: '#D4FF00' }} />
                <span className="text-sm">100% Free</span>
              </div>
            </div>

            {wantsCall === null && (
              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <button
                  onClick={() => setWantsCall(true)}
                  className="flex-1 p-6 rounded-xl border-2 border-[#D4FF00] bg-[#D4FF00]/10 hover:bg-[#D4FF00]/20 transition-all text-center"
                  data-testid="consultation-wants-call-yes"
                >
                  <Check size={32} className="mx-auto mb-3 text-[#D4FF00]" />
                  <span className="font-bold text-lg">Yes, book a call</span>
                  <p className="text-sm text-zinc-500 mt-2">Schedule a time that works for you</p>
                </button>
                <button
                  onClick={() => setWantsCall(false)}
                  className="flex-1 p-6 rounded-xl border border-white/20 hover:border-white/40 transition-all text-center"
                  data-testid="consultation-wants-call-no"
                >
                  <ArrowRight size={32} className="mx-auto mb-3 text-zinc-500" />
                  <span className="font-bold text-lg">No, just submit</span>
                  <p className="text-sm text-zinc-500 mt-2">We'll get back to you via email</p>
                </button>
              </div>
            )}

            {wantsCall === true && (
              <div className="mb-8">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <div>
                    <h3 className="font-bold mb-4">Select a date</h3>
                    <div className="flex justify-center">
                      <CalendarComponent
                        mode="single"
                        selected={selectedDate}
                        onSelect={setSelectedDate}
                        disabled={disabledDays}
                        className="rounded-xl border border-white/10 bg-black/20 p-4"
                        classNames={{
                          months: "flex flex-col",
                          month: "space-y-4",
                          caption: "flex justify-center pt-1 relative items-center",
                          caption_label: "text-lg font-semibold",
                          nav: "space-x-1 flex items-center",
                          nav_button: "h-9 w-9 bg-white/10 hover:bg-[#D4FF00] hover:text-black rounded-lg flex items-center justify-center transition-colors",
                          nav_button_previous: "absolute left-1",
                          nav_button_next: "absolute right-1",
                          table: "w-full border-collapse",
                          head_row: "flex justify-between",
                          head_cell: "text-zinc-500 rounded-md w-10 font-medium text-sm",
                          row: "flex w-full mt-2 justify-between",
                          cell: "relative p-0 text-center text-sm",
                          day: "h-10 w-10 p-0 font-normal rounded-lg hover:bg-white/10 flex items-center justify-center transition-colors",
                          day_selected: "bg-[#D4FF00] text-black hover:bg-[#D4FF00] hover:text-black font-bold",
                          day_today: "border border-[#D4FF00] text-[#D4FF00]",
                          day_outside: "text-zinc-700 opacity-50",
                          day_disabled: "text-zinc-700 opacity-30 cursor-not-allowed hover:bg-transparent",
                        }}
                      />
                    </div>
                  </div>
                  <div>
                    <h3 className="font-bold mb-4">Select a time</h3>
                    {selectedDate ? (
                      <div className="grid grid-cols-2 gap-3">
                        {timeSlots.map((time) => (
                          <button
                            key={time}
                            type="button"
                            onClick={() => setSelectedTime(time)}
                            className={`px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                              selectedTime === time
                                ? 'bg-[#D4FF00] text-black'
                                : 'bg-white/5 hover:bg-white/10 border border-white/10'
                            }`}
                            data-testid={`time-slot-${time.replace(/\s/g, '-')}`}
                          >
                            {time}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <div className="flex items-center justify-center h-full min-h-[200px] rounded-xl border border-dashed border-white/20">
                        <p className="text-zinc-500">Please select a date first</p>
                      </div>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => setWantsCall(null)}
                  className="text-sm text-zinc-500 hover:text-white mt-6 underline"
                >
                  Changed my mind, skip the call
                </button>
              </div>
            )}

            {(wantsCall === false || (wantsCall === true && selectedDate && selectedTime)) && (
              <form onSubmit={handleSubmit}>
                {wantsCall && selectedDate && selectedTime && (
                  <div
                    className="flex items-center gap-4 p-4 rounded-xl mb-6"
                    style={{ background: 'rgba(212, 255, 0, 0.05)', border: '1px solid rgba(212, 255, 0, 0.2)' }}
                  >
                    <Calendar size={24} style={{ color: '#D4FF00' }} />
                    <div>
                      <p className="text-sm" style={{ color: '#A1A1AA' }}>Your consultation</p>
                      <p className="font-bold">
                        {selectedDate?.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })} at {selectedTime}
                      </p>
                    </div>
                  </div>
                )}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary w-full justify-center disabled:opacity-50"
                  data-testid="consultation-submit-btn"
                >
                  {isSubmitting ? 'Submitting...' : wantsCall ? 'Submit & Book Consultation' : 'Submit Request'}
                </button>
              </form>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
